import { CoreEvent, eventNames, useCoreEvents } from '@carvana/core-events';
import { useCallback, useContext, useEffect } from 'react';
import { Consumer } from '~/constants/common/consumer';
import { useIsNative } from '~/context/common/DeviceContext';
import { useEventCorrelationId } from '~/context/common/StaticContext';
import { useUserLocation } from '~/context/common/UserContext';
import { useIsComposerReady } from '~/context/composer/ComposerContext';
import ModalContext from '~/context/home/ModalContext';
import { ComposerModalName } from '~/context/home/ModalContext/constants';
import {
  SoftPullLocations,
  useOpenSoftPullModal
} from '~/hooks/home/useToggleSoftPullModal';
import buildComposerRedirectUrl from '~/utilities/composer/buildComposerRedirectUrl';
import buildComposerSuggestionInfo from '~/utilities/composer/buildComposerSuggestionInfo';
import parseComposerFilters from '~/utilities/composer/parseComposerFilters';
import { logError } from '~/utilities/loggers';

export default function useComposerEvents() {
  const { setComposerUiModalOpen } = useContext(ModalContext);
  const openSoftPullModal = useOpenSoftPullModal(SoftPullLocations.Composer);
  const eventCorrelationId = useEventCorrelationId();
  const isComposerReady = useIsComposerReady();
  const isNative = useIsNative();
  const { zip } = useUserLocation();

  // Listeners
  useCoreEvents(
    eventNames.composer.mfe.opened,
    useCallback(
      ({ source }) => {
        if (source !== 'composer') return;

        setComposerUiModalOpen(true);
      },
      [setComposerUiModalOpen]
    )
  );
  useCoreEvents(
    eventNames.composer.mfe.closed,
    useCallback(
      ({ source }) => {
        if (source !== 'composer') return;

        setComposerUiModalOpen(false);
      },
      [setComposerUiModalOpen]
    )
  );
  useCoreEvents(
    eventNames.composer.mfe.openPageModal,
    useCallback(
      ({ modal, source }) => {
        if (source !== 'composer') return;

        switch (modal) {
          case ComposerModalName.SoftPull:
            openSoftPullModal();
            break;
          default:
            logError(new Error('Unknown modal'), { modal });
        }
      },
      [openSoftPullModal]
    )
  );
  useCoreEvents(
    eventNames.composer.mfe.updateSearch,
    useCallback(
      ({ cvnaid, filters, source, suggestionInfo }) => {
        if (source !== 'composer' || isNative) return;

        const composerSuggestionInfo =
          suggestionInfo && buildComposerSuggestionInfo(suggestionInfo);

        if (cvnaid) {
          window.location.href = buildComposerRedirectUrl(Consumer.HOME, {
            cvnaid,
            suggestionInfo: composerSuggestionInfo
          });

          return;
        }

        const validatedFilters = parseComposerFilters(filters);
        if (!validatedFilters) return;

        window.location.href = buildComposerRedirectUrl(Consumer.HOME, {
          filters: validatedFilters,
          suggestionInfo: composerSuggestionInfo
        });
      },
      [isNative]
    )
  );
  // Non-SPA nav (Home is Pages Router, VDP is App Router), so this unmounts
  // everything on navigation — no need to explicitly close composer first,
  // unlike the SPA case on SRP.
  useCoreEvents(
    eventNames.composer.mfe.vdp,
    useCallback(
      ({ source, vehicleId }) => {
        if (source !== 'composer' || isNative || !vehicleId) return;

        window.location.href = `/vehicle/${vehicleId}`;
      },
      [isNative]
    )
  );

  // Dispatchers
  useEffect(() => {
    if (!isComposerReady) return;

    CoreEvent.emit(eventNames.composer.pageContext, {
      context: {
        capabilities: ['handle', 'openPageModal', 'update_search', 'vdp'],
        merchContextId: eventCorrelationId,
        supportedModals: [ComposerModalName.SoftPull],
        url: window.location.href,
        zip5: zip
      },
      source: 'page',
      surface: 'home'
    });
  }, [eventCorrelationId, isComposerReady, zip]);
}
